import React from 'react';
import { FiMic, FiMicOff, FiX, FiStar } from 'react-icons/fi';
import './Modals.css';
import { useLanguage } from '../../context/LanguageContext';

const asTextList = (value) => {
  if (Array.isArray(value)) {
    return value.filter((item) => item != null && item !== '').map((item) =>
      typeof item === 'string' ? item : JSON.stringify(item)
    );
  }
  if (value == null || value === '') return [];
  return [typeof value === 'string' ? value : JSON.stringify(value)];
};

const EvaluationModal = ({
  isOpen,
  onClose,
  isEvaluating,
  diagnosis,
  managementPlan,
  evaluationResult,
  onDiagnosisChange,
  onManagementPlanChange,
  onSubmitEvaluation,
  onStartNewCase
}) => {
  const { language, t } = useLanguage();
  const [isListening, setIsListening] = React.useState(false);
  const recognitionRef = React.useRef(null);

  React.useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = { en: 'en-US', ru: 'ru-RU', uz: 'uz-UZ' }[language] || 'en-US';

      recognitionRef.current.onresult = (event) => {
        let transcript = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          if (event.results[i].isFinal) {
            transcript += event.results[i][0].transcript + ' ';
          }
        }
        if (transcript) {
          onManagementPlanChange(prev => (prev || '') + transcript);
        }
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
      };
    }
  }, [onManagementPlanChange, language]);

  const toggleListening = () => {
    if (!recognitionRef.current) {
      alert(t('speechUnsupported'));
      return;
    }

    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      try {
        recognitionRef.current.start();
        setIsListening(true);
      } catch (error) {
        console.error("Failed to start speech recognition:", error);
        setIsListening(false);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h2>{t('submitDiagnosis')}</h2>
          <button className="close-modal" onClick={onClose}>
            <FiX size={24} />
          </button>
        </div>

        <div className="modal-body">
          {isEvaluating ? (
            <div className="evaluation-loading">
              <div className="loading-spinner"></div>
              <h3>{t('evaluationProgress')}</h3>
              <p>{t('aiAnalyzing')}</p>
              <div className="loading-steps">
                <div className="loading-step">✓ {t('analyzingDiagnosis')}</div>
                <div className="loading-step">✓ {t('reviewingPlan')}</div>
                <div className="loading-step active">{t('generatingFeedback')}</div>
              </div>
            </div>
          ) : evaluationResult ? (
            <div className="evaluation-result">
              <div className="score-card">
                <h3>{t('encounterCompleted')}</h3>
                <div className="score-display">
                  <div className="score-circle">
                    <span className="score-number">
                      {evaluationResult.total_score ?? evaluationResult.score}
                    </span>
                    <span className="score-total">/100</span>
                  </div>
                  <div className="score-label">{t('overallScore')}</div>
                </div>

                {evaluationResult.breakdown ? (
                  <div className="score-breakdown">
                    {[
                      { key: 'history',    label: 'History Taking',   color: '#3b82f6' },
                      { key: 'diagnosis',  label: 'Diagnosis',         color: '#8b5cf6' },
                      { key: 'management', label: 'Management Plan',   color: '#10b981' },
                    ].map(({ key, color }) => {
                      const label = { history: t('historyTaking'), diagnosis: t('diagnosis'), management: t('management') }[key];
                      const sec = evaluationResult.breakdown?.[key] || {};
                      const sectionMax = Number(sec.max) || ({ history: 30, diagnosis: 40, management: 30 }[key]);
                      const sectionScore = Number(sec.score) || 0;
                      const achieved = asTextList(sec.achieved);
                      const partial = asTextList(sec.partial);
                      const missed = asTextList(sec.missed);
                      const lostMarks = asTextList(sec.lost_marks_reason);
                      const improvements = asTextList(sec.improvements);
                      const deductionDetails = lostMarks.length ? lostMarks : missed;
                      const missedDetails = lostMarks.length ? missed : [];
                      const pct = sectionMax > 0 ? Math.max(0, Math.min(100, Math.round((sectionScore / sectionMax) * 100))) : 0;
                      return (
                        <div className="breakdown-section" key={key}>
                          <div className="breakdown-header">
                            <span className="breakdown-label">{label}</span>
                            <span className="breakdown-score" style={{ color }}>
                              {sectionScore}/{sectionMax}
                            </span>
                          </div>
                          <div className="breakdown-bar-bg">
                            <div
                              className="breakdown-bar-fill"
                              style={{ width: `${pct}%`, background: color }}
                            />
                          </div>
                          {achieved.length > 0 && (
                            <ul className="checklist-list achieved">
                              {achieved.map((it, i) => (
                                <li key={i}><span className="check-icon">✓</span>{it}</li>
                              ))}
                            </ul>
                          )}
                          {partial.length > 0 && (
                            <ul className="checklist-list partial">
                              {partial.map((it, i) => (
                                <li key={i}><span className="partial-icon">◐</span>{it}</li>
                              ))}
                            </ul>
                          )}
                          {deductionDetails.length > 0 && (
                            <ul className="checklist-list missed">
                              {deductionDetails.map((it, i) => (
                                <li key={i}><span className="cross-icon">✗</span>{it}</li>
                              ))}
                            </ul>
                          )}
                          {missedDetails.length > 0 && (
                            <ul className="checklist-list missed-detail">
                              {missedDetails.map((it, i) => (
                                <li key={i}><span className="dot-icon">•</span>{it}</li>
                              ))}
                            </ul>
                          )}
                          {improvements.length > 0 && (
                            <ul className="checklist-list improvements">
                              {improvements.map((it, i) => (
                                <li key={i}><span className="info-icon">→</span>{it}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="feedback-card">
                    <h4>{t('defaultFeedback')}</h4>
                    <div className="feedback-text">{evaluationResult.feedback}</div>
                  </div>
                )}
              </div>
              <div className="modal-actions">
                <button className="btn-primary" onClick={onStartNewCase || onClose}>
                  {t('chooseAnother')}
                </button>
                <button className="btn-secondary" onClick={onClose}>
                  {t('cancel')}
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="form-section">
                <label>{t('yourDiagnosis')}:</label>
                <textarea
                  value={diagnosis}
                  onChange={(e) => onDiagnosisChange(e.target.value)}
                  placeholder={t('diagnosisPlaceholder')}
                  rows="3"
                  className="diagnosis-input"
                />
              </div>

              <div className="form-section">
                <label>{t('managementPlan')}:</label>
                <textarea
                  value={managementPlan}
                  onChange={(e) => onManagementPlanChange(e.target.value)}
                  placeholder={t('planPlaceholder')}
                  rows="4"
                  className="management-input"
                />
                <button
                  type="button"
                  className={`mic-btn-floating ${isListening ? 'listening' : ''}`}
                  onClick={toggleListening}
                  title={isListening ? t('stopVoice') : t('voiceInput')}
                >
                  {isListening ? <><FiMicOff size={16} /> {t('stopVoice')}</> : <><FiMic size={16} /> {t('voiceInput')}</>}
                </button>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-primary submit-btn"
                  onClick={onSubmitEvaluation}
                  disabled={!diagnosis.trim() || !managementPlan.trim() || isEvaluating}
                >
                  <FiStar size={18} />
                  <span>{t('submitEvaluation')}</span>
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={onClose}
                >
                  {t('cancel')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EvaluationModal;
